import type { ReactNode, Ref } from 'react';
import styles from './DropdownMenuItem.module.css';

/**
 * Figma: `_Dropdown list item` (984:36265) on the `Dropdown` page (984:36121).
 *
 * The `_` prefix marks it internal, so it is not part of the package surface —
 * compose it through `Dropdown`.
 *
 * Figma nests the row two deep: the component root is a FILL-width flex wrapper
 * with zero padding, no fill and no radius, and every visible property —
 * padding, radius and the hover surface — lives on its `Content` child. The two
 * are collapsed into one element here because the root contributes nothing to
 * the rendered box; `Content` *is* the row.
 *
 * Variants are `State=Default|Hover/active|Disabled`. Only `Disabled` becomes a
 * prop: hover is CSS, and Figma's "Hover/active" is one variant covering both,
 * so there is no separate active appearance to model.
 */
export type DropdownMenuItemState = 'default' | 'hover';

export type DropdownMenuItemProps = {
  /** Figma `Text`. */
  label: string;
  /**
   * Figma `🔀 Icon swap` (instance swap) — a 12x12 slot before the label. The
   * default swap is the generic `placeholder` component, so the design shows a
   * slot rather than a specific icon; omit it to render label-only.
   */
  icon?: ReactNode;
  /** Figma `State=Disabled`. */
  disabled?: boolean;
  onSelect?: () => void;
  /** Force the hover appearance for documentation and visual regression only. */
  state?: DropdownMenuItemState;
  id?: string;
  className?: string;
  /**
   * Not in Figma. `Dropdown` runs a roving tabindex over its rows, so the menu
   * is one tab stop and the arrow keys move within it — which is what
   * `role="menu"` promises a keyboard user.
   */
  tabIndex?: number;
  /** So `Dropdown` can move focus onto a row. Not in Figma. */
  ref?: Ref<HTMLButtonElement>;
};

export function DropdownMenuItem({
  label,
  icon,
  disabled = false,
  onSelect,
  state,
  id,
  className,
  tabIndex,
  ref,
}: DropdownMenuItemProps) {
  const classes = [
    styles.row,
    state === 'hover' ? styles.hover : null,
    // Live hover only when no state is forced, so `:hover` reacts in the app and
    // stays inert in a states story.
    state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    /*
     * Not in Figma — Figma has no concept of an element type. A menu command is
     * a button, so it gets keyboard activation, a disabled state assistive tech
     * can read, and a focus ring, none of which a styled `<div>` would have.
     */
    <button
      type="button"
      role="menuitem"
      className={classes}
      disabled={disabled}
      onClick={onSelect}
      id={id}
      tabIndex={tabIndex}
      ref={ref}
    >
      {/* Figma `Icon and text` (984:36296). */}
      <span className={styles.textAndIcon}>
        {icon !== undefined && (
          /* Decorative — the label beside it already names the command. */
          <span className={styles.iconSlot} aria-hidden>
            {icon}
          </span>
        )}
        <span className={styles.label}>{label}</span>
      </span>
    </button>
  );
}
